import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { H3 } from '@frontend/shadcn/components/typography/h3';
import { PlusIcon, XmarkIcon as XmarkIconRegular } from '@revfluence/fresh-icons/regular/esm';
import {
  GripDotsVerticalIcon,
  CircleExclamationIcon,
  ShuffleIcon,
  GearIcon,
  XmarkIcon,
} from '@revfluence/fresh-icons/solid/esm';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { Tag } from '@frontend/app/refresh-components';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { DragDropContext, Droppable, Draggable, DropResult, DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { cn } from '@frontend/shadcn/lib/utils';
import { toast } from '@frontend/shadcn/hooks/use-toast';
import { logger } from '@common';
import { useUpdateCreatorTasksConfig } from '@frontend/app/hooks/useUpdateCreatorTasksConfig';
import { GET_CREATOR_TASKS_CONFIG } from '@frontend/app/queries/CreatorTasksConfig';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@frontend/shadcn/components/ui/select';
import { Input } from '@frontend/shadcn/components/ui/input';
import { message, Modal } from '@revfluence/fresh';
import { MagnifyingGlassIcon, CheckIcon } from '@revfluence/fresh-icons/regular/esm';
import { sortBy } from 'lodash';
import { useGetAllWorkletsQuery, useGetCountsForProjectQuery, useDeleteStage as useDeleteCustomStage, useClientFeatureEnabled, useGetInstalledApplicationIds } from '@frontend/app/hooks';
import { P } from '@frontend/shadcn/components/typography/p';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { useHistory } from 'react-router-dom';
import { useAppContext } from '@frontend/context/AppContext';
import { useApolloClient, useGetOffersBySearchQuery } from '@frontend/applications/AffiliatesApp/hooks';
import { OFFER_TYPE } from '@frontend/applications/AffiliatesApp/types/globalTypes';
import { useGetProjectConfigByType } from '@frontend/applications/ProductFulfillmentApp/hooks/useGetProjectConfigByType';
import { ProjectConfigType } from '@frontend/applications/ProductFulfillmentApp/types/globalTypes';
import { useMessagingContext } from '@frontend/hooks';
import { reduce } from 'lodash';
import { ClientFeature } from '@frontend/app/constants';
import { CONTRACT_APP_ID } from '@frontend/app/constants/applicationIds';
import { StageType } from '@frontend/app/types/globalTypes';
import type { GetStageTemplates_getStageTemplates as IStageTemplate } from '@frontend/app/queries/types/GetStageTemplates';
import { GetAllStagesQuery, GetAllStagesQuery_getAllStages, GetAllStagesQueryVariables } from '@frontend/app/queries/types/GetAllStagesQuery';
import { GetAllWorkletsForParentSpecQuery, GetAllWorkletsForParentSpecQuery_worklets, GetAllWorkletsForParentSpecQueryVariables } from '@frontend/app/queries/types/GetAllWorkletsForParentSpecQuery';
import { ApolloQueryResult } from '@apollo/client';
import { useCampaign, CreatorTaskConfig, TWorkletWithoutTypename } from '../CampaignContext';
import { StageToCreatorInteractionMap } from '../components/TaskItem';
import { FilledIconContainer, getNewStageIconsFilled, getNewStageIcons, WorkletSpecUri, Task } from '../../constants';
import { useCampaignForm } from '../hooks/useCampaignForm';
import { TProject } from '../../types';
import { WorkletTypes, CustomWorkletTemplate } from '../constants';
import { useDeleteStage } from '../../hooks/useDeleteStage';
import { NewDeleteStageModal } from '../../ProjectDetailsForm/NewDeleteStageModal';
import { StageTemplateSelector } from '../components/StageTemplateSelector';

interface WorkletWithHiddenFlag extends TWorkletWithoutTypename {
  isHidden?: boolean;
}

interface EditableWorklet extends TWorkletWithoutTypename {
  isEditing?: boolean;
  isCustom?: boolean;
  templateData?: IStageTemplate;
  stageData?: {
    name: string;
    programId: number;
    type: StageType;
    stageTemplateId?: number;
    statuses: Array<{
      name: string;
      priority: number;
      metadata: {
        bgColor: string;
      };
    }>;
    tasks: Array<{
      name: string;
      ctaLabel: string;
      fields: Array<{
        name: string;
        type: string;
      }>;
    }>;
  };
}

interface ConsolidatedWorkflowSectionProps {
  mode?: 'add' | 'edit';
  project?: TProject;
  defaultWorklets?: TWorkletWithoutTypename[];
  parentSpecKey?: string;
  projectId?: number;
  onSave?: () => void;
  refetchWorklets?: (variables?: Partial<GetAllWorkletsForParentSpecQueryVariables>) => Promise<ApolloQueryResult<GetAllWorkletsForParentSpecQuery>>;
  refetchProjects?: () => void;
  isCreatorTasksConfigLoading?: boolean;
  initTasks?: (newWorklets?: GetAllWorkletsForParentSpecQuery_worklets[]) => void;
  customStages?: GetAllStagesQuery_getAllStages[];
  refetchCustomStages?: (variables?: Partial<GetAllStagesQueryVariables>) => Promise<ApolloQueryResult<GetAllStagesQuery>>
}

const NumberIcon = ({ number }: { number: number }) => {
  return (
    <div className="flex items-center justify-center rounded-full w-4 h-4 text-[10px] font-extrabold bg-grey-5 text-white">{number}</div>
  );
};

interface SetupNeededButtonProps {
  workletSpecURI: string;
  projectId: number;
  isSubmitting?: boolean;
}

const SetupNeededButton = ({ workletSpecURI, projectId, isSubmitting = false }: SetupNeededButtonProps) => {
  const history = useHistory();
  const { pfaApolloClient } = useAppContext();
  const staApolloClient = useApolloClient();

  const { data: connectedOffers, loading: connectedOffersLoading } = useGetOffersBySearchQuery({
    variables: {
      query: {
        programId: projectId,
      },
    },
    client: staApolloClient,
    skip: ![
      WorkletSpecUri.PromoCodesWorkletSpecification,
      WorkletSpecUri.SalesLinkWorkletSpecification,
      WorkletSpecUri.SalesLinkPromoCodesWorkletSpecification,
    ].includes(workletSpecURI as WorkletSpecUri),
    fetchPolicy: 'network-only',
  });

  const { hasConnectedPromoCode, hasConnectedSalesLink } = useMemo(
    () => ({
      hasConnectedPromoCode: connectedOffers?.offers.some((offer) => offer.type === OFFER_TYPE.PROMO_CODE),
      hasConnectedSalesLink: connectedOffers?.offers.some((offer) => offer.type === OFFER_TYPE.LINK),
    }),
    [connectedOffers],
  );

  const { projectConfig, loading: projectConfigLoading } = useGetProjectConfigByType({
    variables: {
      projectId,
      type: ProjectConfigType.ProductCatalog,
    },
    skip: workletSpecURI !== WorkletSpecUri.SendBrandProductCatalogWorkletSpecification,
    client: pfaApolloClient,
  });

  const isLoading = connectedOffersLoading || projectConfigLoading;

  const setupNeeded = useMemo(() => {
    if (isLoading) {
      return false;
    }
    if (workletSpecURI === WorkletSpecUri.PromoCodesWorkletSpecification) {
      return !hasConnectedPromoCode;
    }
    if (workletSpecURI === WorkletSpecUri.SalesLinkWorkletSpecification) {
      return !hasConnectedSalesLink;
    }
    if (workletSpecURI === WorkletSpecUri.SalesLinkPromoCodesWorkletSpecification) {
      return !hasConnectedPromoCode || !hasConnectedSalesLink;
    }
    if (workletSpecURI === WorkletSpecUri.SendBrandProductCatalogWorkletSpecification) {
      return !projectConfig?.brandCatalogs?.length;
    }
    return false;
  }, [hasConnectedPromoCode, hasConnectedSalesLink, isLoading, projectConfig?.brandCatalogs?.length, workletSpecURI]);

  if (![
    WorkletSpecUri.PromoCodesWorkletSpecification,
    WorkletSpecUri.SalesLinkWorkletSpecification,
    WorkletSpecUri.SalesLinkPromoCodesWorkletSpecification,
    WorkletSpecUri.SendBrandProductCatalogWorkletSpecification
  ].includes(workletSpecURI as WorkletSpecUri)) {
    return null;
  }

  const handleSetupClick = () => {
    if ([
      WorkletSpecUri.PromoCodesWorkletSpecification,
      WorkletSpecUri.SalesLinkWorkletSpecification,
      WorkletSpecUri.SalesLinkPromoCodesWorkletSpecification,
    ].includes(workletSpecURI as WorkletSpecUri)) {
      history.push(`/projects/${projectId}/settings/offers`);
    } else {
      history.push(`/projects/${projectId}/settings/product_catalog`);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {setupNeeded && (
        <Badge className="bg-yellow-2 text-primary hover:bg-yellow-2/80" variant="secondary">
          Setup Needed
        </Badge>
      )}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Button
              variant="ghost"
              size="xs"
              loading={isLoading}
              disabled={isSubmitting}
              onClick={handleSetupClick}
            >
              <GearIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            Configure settings
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

const StageItem = ({
  worklet,
  dragHandleProps,
  onRemove,
  showRemoveButton = false,
  isHidden = false,
  projectId,
  existingWorklet = false,
  deletingWorkletIndex,
  isCountsLoading = false,
  workletIndex,
  isSubmitting = false,
}: {
  worklet: WorkletWithHiddenFlag;
  dragHandleProps: DraggableProvidedDragHandleProps | null;
  onRemove?: () => void;
  showRemoveButton?: boolean;
  isHidden?: boolean;
  projectId?: number;
  existingWorklet?: boolean;
  deletingWorkletIndex?: number | null;
  isCountsLoading?: boolean;
  workletIndex?: number;
  isSubmitting?: boolean;
}) => {
  const title = worklet.specTitle;
  const stageIcon = getNewStageIconsFilled('h-6 w-6')[worklet.specURI] || (
    <FilledIconContainer className="bg-grey-4">
      <ShuffleIcon />
    </FilledIconContainer>
  );

  return (
    <div className="flex items-center gap-2 w-full">
      <div {...dragHandleProps} className="flex items-center justify-center">
        <GripDotsVerticalIcon className="text-grey-3" fontSize={16} />
      </div>
      <div className="flex items-center gap-2 flex-1">
        {stageIcon}
        <span className="font-medium">{title}</span>
        {isHidden && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="ml-auto">
                <Badge className="bg-grey-2 text-grey-6 hover:bg-grey-2/80" variant="secondary">
                  Hidden
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                Internal use only. This stage is hidden from creators in the portal.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <div className="flex items-center gap-2">
        {(projectId && existingWorklet) && <SetupNeededButton workletSpecURI={worklet.specURI} projectId={projectId} isSubmitting={isSubmitting} />}
        {(showRemoveButton || existingWorklet) && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={onRemove}
                  loading={deletingWorkletIndex === workletIndex || isCountsLoading}
                  disabled={isSubmitting || (deletingWorkletIndex !== null && deletingWorkletIndex !== workletIndex)}
                  className="text-danger"
                >
                  {deletingWorkletIndex !== workletIndex && <XmarkIcon />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Remove stage
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
};

export const ConsolidatedWorkflowSection: React.FC<ConsolidatedWorkflowSectionProps> = ({
  mode = 'edit',
  project,
  defaultWorklets,
  parentSpecKey,
  projectId,
  onSave,
  refetchWorklets,
  refetchProjects,
  isCreatorTasksConfigLoading = false,
  initTasks,
  customStages,
  refetchCustomStages,
}) => {
  const {
    worklets,
    initialTasks,
    remainingTasks,
    reorderTasks,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    setWorklets,
    setTasksConfig,
  } = useCampaign();

  const [addingToSection, setAddingToSection] = useState<'prerequisite' | 'flexible' | null>(null);
  const [newStage, setNewStage] = useState<EditableWorklet | null>(null);
  const [workletSearchTerm, setWorkletSearchTerm] = useState('');
  const [deletingWorkletIndex, setDeletingWorkletIndex] = useState<number | null>(null);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);

  const { updateCreatorTasksConfig, isUpdateCreatorTasksConfigLoading } = useUpdateCreatorTasksConfig();
  const { handleCreateOrUpdateCampaign, isSubmitting } = useCampaignForm(mode, project, onSave, refetchProjects);

  // Get all available worklet types for stage selection
  const { data: allWorkletTypes } = useGetAllWorkletsQuery();

  // Feature flags
  const isCustomStagesEnabled = useClientFeatureEnabled(ClientFeature.CUSTOM_STAGES);
  const installedApps = useGetInstalledApplicationIds();

  // Member counts for delete functionality
  const { data, loading: isCountsLoading, refetch: refetchCounts } = useGetCountsForProjectQuery({
    variables: { projectId },
    skip: !projectId,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const counts = data?.counts;

  // Messaging context for delete notifications
  const { showSuccessMessage, showErrorMessage } = useMessagingContext();

  // Custom stage deletion
  const { handleDeleteStage } = useDeleteCustomStage();

  // Delete completion callback
  const onDeleteComplete = async (newWorklets: GetAllWorkletsForParentSpecQuery_worklets[], deletedWorkletSpecKey: string) => {
    // Delete the custom stage
    if (isCustomStagesEnabled && projectId && deletedWorkletSpecKey) {
      const worklet = workletsWithHiddenFlag.find((w) => w.specKey === deletedWorkletSpecKey);
      if (worklet?.specURI?.startsWith('CustomWorklet-')) {
        await handleDeleteStage(projectId, worklet.specTitle);
      }
    }

    setWorklets(newWorklets);
    initTasks(newWorklets);
    handleCreateOrUpdateCampaign(newWorklets);
    setDeletingWorkletIndex(null);
    updateCreatorTasksConfig({
      variables: {
        programId: projectId,
        tasks: [...(initialTasks || []), ...(remainingTasks || [])]
        .filter((task) => newWorklets.some((w) => (w.specURI === task.specURI) || (w.specTitle === task.specTitle)))
        .map((task, index) => ({
          id: task.id,
          specURI: task.specURI,
          isInitial: task.isInitial,
          order: index + 1,
          programId: projectId,
        })),
      },
      refetchQueries: [GET_CREATOR_TASKS_CONFIG],
    }).catch((error) => {
      logger.error('Failed to update creator tasks config', error);
    });
  };

  // Delete stage hook
  const {
    modalVisible: deleteStageModalVisible,
    status: deletingStatus,
    completedPercent: deletingPercentage,
    totalCount: currentStageMemberCount,
    handleRemoveExistingWorklet,
    hideDeleteStageModal,
    skipWorklet,
  } = useDeleteStage(parentSpecKey, projectId, showSuccessMessage, showErrorMessage, onDeleteComplete);

  const sortedWorklets = React.useMemo(() => {
    return sortBy(allWorkletTypes?.worklets, 'specTitle');
  }, [allWorkletTypes]);

  // Create worklets with hidden flag based on creator interaction mapping
  const workletsWithHiddenFlag: WorkletWithHiddenFlag[] = React.useMemo(() => {
    return (worklets || []).map((worklet) => ({
      ...worklet,
      isHidden: worklet?.specURI?.startsWith('CustomWorklet-') ? !customStages?.find((stage) => stage.name === worklet.specTitle)?.tasks[0]?.creatorAccess : !StageToCreatorInteractionMap[worklet?.specURI]
    }));
  }, [worklets, customStages]);

  // Calculate member counts for worklets
  const countsByWorklet = useMemo(
    () =>
      reduce(
        workletsWithHiddenFlag,
        (prev, worklet) => {
          if (!counts || !worklet.specURI) {
            return {
              ...prev,
              [worklet.specKey]: 0,
            };
          }

          const workletCounts = counts[worklet.specURI];

          if (!workletCounts) {
            return {
              ...prev,
              [worklet.specKey]: 0,
            };
          }

          // Check if there's a direct count under Task.All
          if (workletCounts[Task.All]?.total !== undefined) {
            return {
              ...prev,
              [worklet.specKey]: workletCounts[Task.All].total,
            };
          }

          // If not, look for task-specific counts and use the first one with a total
          // This handles cases like SendBrandProductCatalogWorkletSpecification
          const taskKeys = Object.keys(workletCounts);
          for (const taskKey of taskKeys) {
            if (workletCounts[taskKey]?.total !== undefined) {
              return {
                ...prev,
                [worklet.specKey]: workletCounts[taskKey].total,
              };
            }
          }

          // If no counts found, return 0
          return {
            ...prev,
            [worklet.specKey]: 0,
          };
        },
        {},
      ),
    [workletsWithHiddenFlag, counts],
  );

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    if (!destination) {
      return;
    }

    setHasUnsavedChanges(true);
    reorderTasks(source, destination);
  };

  const handleSave = useCallback(async () => {
    try {
      // First save the campaign worklets
      await handleCreateOrUpdateCampaign();

      const { data: { worklets: newWorklets } } = await refetchWorklets();

      const { data: { getAllStages: newCustomStages } } = await refetchCustomStages();

      // Then update creator tasks config
      const allTasks = [...(initialTasks || []), ...(remainingTasks || [])];

      await updateCreatorTasksConfig({
        variables: {
          programId: projectId,
          tasks: allTasks.map((task, index) => {
            let specURI = task.specURI;
            if (!specURI && newWorklets?.length) {
              specURI = newWorklets.find((w) => w.specTitle === task.specTitle)?.specURI;
            }
            let stageId: number = undefined;
            if (!stageId && newCustomStages?.length) {
              stageId = newCustomStages.find((s) => s.name === task.specTitle)?.id;
            }
            return {
              id: task.id,
              specURI,
              isInitial: task.isInitial,
              order: index + 1,
              programId: projectId,
              stageId
            };
          }),
        },
        refetchQueries: [GET_CREATOR_TASKS_CONFIG],
      });

      setHasUnsavedChanges(false);
      toast({
        title: 'Workflow Updated',
        description: 'Workflow stages and creator tasks have been updated successfully.',
        variant: 'success',
      });
    } catch (error) {
      logger.error('Failed to update workflow', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update workflow.',
        variant: 'error',
      });
    }
  }, [handleCreateOrUpdateCampaign, refetchWorklets, refetchCustomStages, initialTasks, remainingTasks, updateCreatorTasksConfig, projectId, setHasUnsavedChanges]);

  const handleCancel = useCallback(() => {
    initTasks?.();
    setWorklets(defaultWorklets || [], false);
    setHasUnsavedChanges(false);
  }, [defaultWorklets, initTasks, setHasUnsavedChanges, setWorklets]);

  const handleDeleteWorklet = async (worklet: WorkletWithHiddenFlag, workletIndex: number, existingWorklet = false) => {
    if (mode === 'edit' && existingWorklet) {
      const confirmDelete = async () => {
        setDeletingWorkletIndex(workletIndex);
        await handleRemoveExistingWorklet(worklet.specKey, countsByWorklet[worklet.specKey]);
      };

      if (hasUnsavedChanges) {
        Modal.confirm({
          title: 'Unsaved changes',
          content: 'The unsaved changes will be lost. Please save the changes before deleting the stage or continue anyway.',
          onOk: confirmDelete,
          type: 'error',
          okText: 'Continue Anyway',
        });
      } else {
        await confirmDelete();
      }
      return;
    }

    // For new stages, just remove from worklets array
    const newWorklets = [...(worklets || [])];
    const workletToRemove = newWorklets.find((w) => (w.specURI && w.specURI === worklet.specURI) || (w.specTitle && w.specTitle === worklet.specTitle));
    if (!workletToRemove) return;

    const workletIndexInArray = newWorklets.findIndex((w) => (w.specURI && w.specURI === worklet.specURI) || (w.specTitle && w.specTitle === worklet.specTitle));
    if (workletIndexInArray === -1) return;

    newWorklets.splice(workletIndexInArray, 1);
    setWorklets(newWorklets, true);

    // Also remove from task configurations
    const newInitialTasks = (initialTasks || []).filter((task) => task.specTitle !== workletToRemove.specTitle);
    const newRemainingTasks = (remainingTasks || []).filter((task) => task.specTitle !== workletToRemove.specTitle);
    const allTasks = [...newInitialTasks, ...newRemainingTasks];
    setTasksConfig(allTasks);

    setHasUnsavedChanges(true);
  };

  const handleAddPrerequisiteStage = () => {
    setAddingToSection('prerequisite');
    setNewStage({
      specKey: null,
      specTitle: '',
      specLogoURL: '',
      specURI: '',
      isEditing: true,
    });
  };

  const handleAddFlexibleStage = () => {
    setAddingToSection('flexible');
    setNewStage({
      specKey: null,
      specTitle: '',
      specLogoURL: '',
      specURI: '',
      isEditing: true,
    });
  };

  const handleStageTypeSelect = (type: string) => {
    // Check if required app is installed
    if (type === WorkletTypes.CONTRACT && !installedApps[CONTRACT_APP_ID]) {
      // Show error message
      message.info('Contract app is not installed on your account. Please contact support to get started.', 5);
      return;
    }

    // If Custom is selected and feature flag is enabled, show template selector
    if (type === WorkletTypes.CUSTOM && isCustomStagesEnabled) {
      setNewStage({
        ...CustomWorkletTemplate,
        isEditing: true,
        isCustom: true,
        specKey: null,
        specTitle: '',
      });
      setShowTemplateSelector(true);
      return;
    }

    const selectedWorklet =
      sortedWorklets?.find((w) => w.specTitle === type) ||
      (type === WorkletTypes.CUSTOM ? CustomWorkletTemplate : null);

    if (!selectedWorklet) return;

    setNewStage({
      ...selectedWorklet,
      isEditing: true,
      isCustom: type === WorkletTypes.CUSTOM,
      specKey: type === WorkletTypes.CUSTOM ? null : selectedWorklet.specKey,
      specTitle: type === WorkletTypes.CUSTOM ? newStage?.specTitle || '' : selectedWorklet.specTitle,
    });
  };

  const handleTemplateSelect = (template: IStageTemplate | null) => {
    if (template) {
      // Template selected - use template data
      setNewStage({
        ...CustomWorkletTemplate,
        isEditing: true,
        isCustom: true,
        specKey: null,
        specTitle: template.name, // Use template name
        // Store template data for later use when creating the stage
        templateData: template,
      });
    } else {
      // Blank custom stage selected - show input with default name
      setNewStage({
        ...CustomWorkletTemplate,
        isEditing: true,
        isCustom: true,
        specKey: null,
        specTitle: 'Blank Custom Name', // Pre-fill with default name
      });
    }

    setShowTemplateSelector(false);
  };

  const handleTemplateCancel = () => {
    setShowTemplateSelector(false);
    setNewStage(null);
    setAddingToSection(null);
    setWorkletSearchTerm('');
  };

  const handleSaveNewStage = () => {
    if (newStage?.isCustom && !newStage?.specTitle?.trim()) {
      message.error('Custom stage name is required');
      return;
    }

    if (!newStage?.specKey && !newStage?.isCustom) {
      message.error('Please select a stage type');
      return;
    }

    // Prepare the stage data - start with a copy of newStage
    let stageToAdd = { ...newStage };

    // If this is a custom stage with template data, we need to handle it specially
    if (newStage?.isCustom && newStage?.templateData && projectId) {
      // Create the stage with template data
      const template = newStage.templateData;

      // Convert template data to the format expected by the create stage API
      const stageData = {
        name: newStage.specTitle,
        programId: projectId,
        type: StageType.CUSTOM,
        stageTemplateId: template.id, // Include template ID for analytics
        statuses: template.statuses.map((status, idx) => ({
          name: status.name,
          priority: idx + 1,
          metadata: {
            bgColor: status.bgColor
          }
        })),
        tasks: [{
          name: template.description || newStage.specTitle, // Use template description (task name) or fallback to stage name
          ctaLabel: 'Submit',
          fields: template.fields?.map((field) => ({
            name: field.name,
            type: field.type
          })) || []
        }]
      };

      // Store the stage data for later use when the campaign is saved
      stageToAdd = { ...stageToAdd, stageData };
    }

    // Add the new stage to worklets
    const updatedWorklets = [
      ...(worklets || []),
      {
        ...stageToAdd,
        isEditing: false,
      }
    ];

    // Create the corresponding task
    const newTask: CreatorTaskConfig = {
      specURI: newStage.specURI,
      isInitial: addingToSection === 'prerequisite',
      order: addingToSection === 'prerequisite'
        ? (initialTasks?.length || 0)
        : (initialTasks?.length || 0) + (remainingTasks?.length || 0),
      specTitle: newStage.specTitle,
    };

    // Update task lists
    const updatedInitialTasks = addingToSection === 'prerequisite'
      ? [...(initialTasks || []), newTask]
      : initialTasks || [];

    const updatedRemainingTasks = addingToSection === 'flexible'
      ? [...(remainingTasks || []), newTask]
      : remainingTasks || [];

    // Update campaign context
    setWorklets(updatedWorklets, true);

    // Update tasks configuration via context
    const allTasks = [...updatedInitialTasks, ...updatedRemainingTasks];
    setTasksConfig(allTasks);

    // Reset state
    setAddingToSection(null);
    setNewStage(null);
    setWorkletSearchTerm('');
    setShowTemplateSelector(false);
    setHasUnsavedChanges(true);
  };

  const handleCancelNewStage = () => {
    setAddingToSection(null);
    setNewStage(null);
    setWorkletSearchTerm('');
    setShowTemplateSelector(false);
  };

  useEffect(() => {
    if (defaultWorklets?.length) {
      setWorklets(defaultWorklets, false);
    }
  }, [defaultWorklets, setWorklets]);

  useEffect(() => {
    refetchCounts();
  }, [refetchCounts, defaultWorklets]);

  // Component for editing a new stage
  const renderNewStageEditor = () => {
    if (!newStage) return null;

    const isCustomWorklet = newStage.isCustom || newStage.specURI?.startsWith('CustomWorklet-');

    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <NumberIcon number={addingToSection === 'prerequisite' ? (initialTasks?.length || 0) + 1 :
           (initialTasks?.length || 0) + (remainingTasks?.length || 0) + 1}
          />
          <div className="px-3 border border-border rounded-2xl h-12 max-h-12 flex items-center flex-1 bg-grey-1">
            <div className="flex items-center gap-3 w-full">
              <div className="flex items-center justify-center">
                <GripDotsVerticalIcon className="text-grey-3" fontSize={16} />
              </div>
              <div className="flex items-end gap-4 flex-1">
                <div className="flex-1">
                  <Select
                    value={isCustomWorklet ? WorkletTypes.CUSTOM : newStage.specTitle}
                    onValueChange={handleStageTypeSelect}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Stage" />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="px-2 border-b border-border -mx-1">
                        <div className="relative">
                          <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-primaryMuted" />
                          <Input
                            placeholder="Search for stage"
                            value={workletSearchTerm}
                            onChange={(e) => {
                              e.stopPropagation();
                              setWorkletSearchTerm(e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="pl-8 border-none focus-visible:ring-0"
                            autoFocus
                          />
                        </div>
                      </div>
                      <div className="max-h-[300px] overflow-y-auto">
                        {sortedWorklets
                          ?.filter((type) => type.specTitle.toLowerCase().includes(workletSearchTerm.toLowerCase()))
                          ?.filter((type) => !worklets?.find((w) => w.specKey === type.specKey))
                          .map((type) => (
                            <SelectItem
                              key={type.specKey}
                              value={type.specTitle}
                            >
                              <div className="flex items-center gap-2">
                                {getNewStageIcons()[type.specURI] || <ShuffleIcon className="text-grey-4" />}
                                <span>{type.specTitle}</span>
                              </div>
                            </SelectItem>
                          ))}
                      </div>
                    </SelectContent>
                  </Select>
                </div>

                {isCustomWorklet && !showTemplateSelector && (
                  <div className="flex-1">
                    <Input
                      placeholder="Enter stage name"
                      value={newStage.specTitle || ''}
                      onChange={(e) => {
                        setNewStage({
                          ...newStage,
                          specTitle: e.target.value
                        });
                      }}
                    />
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <Button
                    variant="destructiveOutline"
                    size="smallIcon"
                    onClick={handleCancelNewStage}
                  >
                    <XmarkIconRegular />
                  </Button>
                  <Button
                    size="smallIcon"
                    onClick={handleSaveNewStage}
                    disabled={(isCustomWorklet ? !newStage.specTitle?.trim() : !newStage.specKey) || showTemplateSelector}
                  >
                    <CheckIcon />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Template Selector - shown below the stage editor when Custom is selected */}
        {showTemplateSelector && (
          <div className="ml-8"> {/* Indent to align with the stage editor */}
            <StageTemplateSelector
              onSelectTemplate={handleTemplateSelect}
              onCancel={handleTemplateCancel}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex justify-between">
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <H3 className="mb-0 font-medium">Workflow</H3>
          <div className="flex items-center gap-2">
            {hasUnsavedChanges && (
              <Tag
                label={(
                  <div className="flex items-center gap-1 text-yellow-5">
                    <CircleExclamationIcon className="text-yellow-5" />
                    Unsaved Changes
                  </div>
                )}
                textColor="text-yellow-5"
              />
            )}
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={!hasUnsavedChanges || isSubmitting || isUpdateCreatorTasksConfigLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasUnsavedChanges}
              loading={isSubmitting || isUpdateCreatorTasksConfigLoading}
            >
              Save
            </Button>
          </div>
        </div>

        <DragDropContext onDragEnd={onDragEnd}>
          <Card className="border border-border shadow-none w-[700px]">
            <CardContent className="p-6 flex flex-col gap-4">
              {/* Prerequisite Stages Section */}
              <div className="bg-grey-1 rounded-2xl px-4 py-2">
                <div className="flex flex-row justify-between items-center mb-4">
                  <div className="flex flex-col">
                    <H3 className="font-medium mb-0 text-base leading-7">Prerequisite Stages</H3>
                    <P className="text-primaryMuted leading-6">Members must complete the Prerequisite Stages in the set order to unlock access to any Flexible Stages.</P>
                  </div>
                  <Button
                    variant="outline"
                    size="xs"
                    onClick={handleAddPrerequisiteStage}
                    className="self-start mt-0.5"
                    disabled={addingToSection !== null || isSubmitting || isUpdateCreatorTasksConfigLoading}
                  >
                    <PlusIcon className="mr-2" />
                    Add Prerequisite Stage
                  </Button>
                </div>
                <div className="self-stretch flex flex-col justify-start items-start gap-2">
                  <Droppable droppableId="initialTasks">
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="self-stretch flex flex-col gap-2"
                      >
                        {isCreatorTasksConfigLoading && (
                        <div className="flex flex-col items-center gap-2">
                          <Skeleton className="self-stretch h-12" />
                          <Skeleton className="self-stretch h-12" />
                        </div>
                    )}
                        {!isCreatorTasksConfigLoading &&
                          initialTasks?.map((task, index) => {
                            const worklet = workletsWithHiddenFlag.find((w) => (w.specURI && w.specURI === task.specURI) || (w.specTitle && w.specTitle === task.specTitle));
                            if (!worklet) return null;

                            return (
                              <Draggable key={task.specURI || task.specTitle} draggableId={task.specURI || task.specTitle} index={index} isDragDisabled={isSubmitting || isUpdateCreatorTasksConfigLoading}>
                                {(provided, snapshot) => (
                                  <div className="flex items-center gap-2">
                                    <NumberIcon number={index + 1} />
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      className={cn(
                                        'px-3 border border-border rounded-2xl h-12 max-h-12 flex items-center flex-1',
                                        snapshot.isDragging && 'opacity-50',
                                        'hover:border-primary transition-all',
                                      )}
                                    >
                                      <StageItem
                                        worklet={worklet}
                                        dragHandleProps={provided.dragHandleProps}
                                        onRemove={() => {
                                          const existingWorklet = defaultWorklets?.find((w) => w.specKey === worklet.specKey);
                                          handleDeleteWorklet(worklet, index, !!existingWorklet);
                                        }}
                                        showRemoveButton
                                        isHidden={worklet.isHidden}
                                        projectId={projectId}
                                        existingWorklet={!!defaultWorklets?.find((w) => w.specKey === worklet.specKey)}
                                        deletingWorkletIndex={deletingWorkletIndex}
                                        isCountsLoading={isCountsLoading}
                                        workletIndex={index}
                                        isSubmitting={isSubmitting || isUpdateCreatorTasksConfigLoading}
                                      />
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                        {provided.placeholder}
                        {addingToSection === 'prerequisite' && renderNewStageEditor()}
                        {!isCreatorTasksConfigLoading && (initialTasks?.length || 0) === 0 && addingToSection !== 'prerequisite' && (
                          <div className="self-stretch h-12 px-2 py-3 rounded-2xl border border-dashed border-border flex justify-center items-center gap-2">
                            Drag and drop stages here to convert them to Prerequisite Stages.
                          </div>
                        )}
                      </div>
                )}
                  </Droppable>
                </div>
              </div>

              {/* Flexible Stages Section */}
              <div className="flex flex-row justify-between items-center">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <H3 className="font-medium mb-0 text-base leading-7">
                      Flexible Stages
                    </H3>
                  </div>
                  <P className="text-primaryMuted leading-6">Flexible Stages can be accessed and completed by members at any time, as long as all Prerequisite Stages have been completed.</P>
                </div>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={handleAddFlexibleStage}
                  className="self-start mt-0.5"
                  disabled={addingToSection !== null || isSubmitting || isUpdateCreatorTasksConfigLoading}
                >
                  <PlusIcon className="mr-2" />
                  Add Flexible Stage
                </Button>
              </div>
              <Droppable droppableId="remainingTasks">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="self-stretch flex flex-col gap-2"
                  >
                    {isCreatorTasksConfigLoading && (
                    <div className="flex flex-col items-center gap-2">
                      <Skeleton className="self-stretch h-12" />
                      <Skeleton className="self-stretch h-12" />
                      <Skeleton className="self-stretch h-12" />
                    </div>
                  )}
                    {!isCreatorTasksConfigLoading &&
                      remainingTasks?.map((task, index) => {
                        const worklet = workletsWithHiddenFlag.find((w) => (w.specURI && w.specURI === task.specURI) || (w.specTitle && w.specTitle === task.specTitle));
                         if (!worklet) return null;

                         const workletIndex = index + (initialTasks?.length || 0);

                         return (
                           <Draggable key={task.specURI || task.specTitle} draggableId={task.specURI || task.specTitle} index={index} isDragDisabled={isSubmitting || isUpdateCreatorTasksConfigLoading}>
                             {(provided, snapshot) => (
                               <div className="flex items-center gap-2">
                                 <NumberIcon number={workletIndex + 1} />
                                 <div
                                   ref={provided.innerRef}
                                   {...provided.draggableProps}
                                   className={cn(
                                     'px-3 border border-border rounded-2xl h-12 max-h-12 flex items-center flex-1',
                                     snapshot.isDragging && 'opacity-50',
                                     'hover:border-primary transition-all',
                                   )}
                                 >
                                   <StageItem
                                     worklet={worklet}
                                     dragHandleProps={provided.dragHandleProps}
                                     onRemove={() => {
                                       const existingWorklet = defaultWorklets?.find((w) => w.specKey === worklet.specKey);
                                       handleDeleteWorklet(worklet, workletIndex, !!existingWorklet);
                                     }}
                                     showRemoveButton
                                     isHidden={worklet.isHidden}
                                     projectId={projectId}
                                     existingWorklet={!!defaultWorklets?.find((w) => w.specKey === worklet.specKey)}
                                     deletingWorkletIndex={deletingWorkletIndex}
                                     isCountsLoading={isCountsLoading}
                                     workletIndex={workletIndex}
                                     isSubmitting={isSubmitting || isUpdateCreatorTasksConfigLoading}
                                   />
                                 </div>
                               </div>
                             )}
                           </Draggable>
                         );
                       })}
                    {provided.placeholder}
                    {addingToSection === 'flexible' && renderNewStageEditor()}
                    {!isCreatorTasksConfigLoading && (remainingTasks?.length || 0) === 0 && addingToSection !== 'flexible' && (
                      <div className="self-stretch h-12 px-2 py-3 rounded-2xl border border-dashed border-border flex justify-center items-center gap-2">
                        Drag and drop stages here to convert them to Flexible Stages.
                      </div>
                    )}
                  </div>
              )}
              </Droppable>
            </CardContent>
          </Card>
        </DragDropContext>

        {/* Delete Stage Modal */}
        <NewDeleteStageModal
          open={deleteStageModalVisible}
          count={currentStageMemberCount}
          status={deletingStatus}
          percentage={deletingPercentage}
          onRequestClose={() => {
            setDeletingWorkletIndex(null);
            hideDeleteStageModal();
          }}
          onDelete={skipWorklet}
          isFlexibleSpec
        />
      </div>
    </div>
  );
};
